import React, { useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useNavigationType } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSummaryInfo,
  updateDailySummaryInfo,
  updateMonthlySummaryInfo,
  updateWeeklySummaryInfo,
} from "redux/slices/sales/salesSlice";

import { getCommaNumber } from "utils/format";
import iconChevron from "assets/icons/icon_chevron_black.svg";

import Section from "components/ui/Section";
import BarChart from "components/ui/chart/Bar";
import PeriodList from "./PeriodList";

/**
 * [기간별 매출 개요]
 */

export const PeriodInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    searchPartner,
    searchBranch,
    searchDate,
    searchPeriodType,
    summaryInfo,
  } = useSelector((state) => state.sales);

  const { data, isLoading, isError } = useSelector(
    (state) => state.sales.summaryInfo
  );

  const partnerInfo = useSelector((state) => state.partner);

  useEffect(() => {
    const isUpdate =
      searchPartner != summaryInfo.searchPartner ||
      searchBranch != summaryInfo.searchBranch ||
      searchDate != summaryInfo.searchDate ||
      searchPeriodType != summaryInfo.searchPeriodType;
    if (isUpdate) eventHandler.update();
  }, [searchPartner, searchBranch, searchDate, searchPeriodType]);

  const eventHandler = {
    update: () => {
      let updateAction = undefined;
      switch (searchPeriodType) {
        case "daily":
          updateAction = updateDailySummaryInfo;
          break;
        case "weekly":
          updateAction = updateWeeklySummaryInfo;
          break;
        case "monthly":
          updateAction = updateMonthlySummaryInfo;
          break;
      }
      dispatch(
        updateAction(
          {
            searchPartner: searchPartner,
            searchBranch: searchBranch,
            searchDate: searchDate,
          },
          fetchSummaryInfo,
          "summaryInfo"
        )
      );
    },
    detail: () => {
      const isBranch =
        partnerInfo.branchUseYn == "Y" && partnerInfo.seqPartnerBranch == 0;
      if (isBranch) {
        navigate("/sales/branch/" + searchPeriodType);
      } else {
        navigate("/sales/" + searchPeriodType);
      }
    },
  };

  const render = () => {
    const totalInfo = (data || {}).totalInfo || {};
    const summaryInfo = (data || {}).summaryInfo || {};
    const chartInfo = (data || {}).chartInfo || {};

    const getLabel = () => {
      switch (searchPeriodType) {
        case "daily":
          return "일간 매출";
        case "weekly":
          return "주간 매출";
        case "monthly":
          return "월간 매출";
      }
    };

    return (
      <Section
        isLoading={isLoading}
        isError={isError}
        onRefresh={eventHandler.update}
      >
        <Wrapper>
          <div className="top">
            <div className="summaryInfo">
              <h4>{getLabel()}</h4>
              <a onClick={eventHandler.detail}>
                <h2>{getCommaNumber(summaryInfo.totalSalesAmount)}원</h2>
              </a>
            </div>
            <div className="chartInfo">
              <BarChart data={chartInfo} height={150} />
            </div>
          </div>
          <div className="middle color">
            <ul>
              <li>
                <div>b.pay</div>
                <div>{getCommaNumber(summaryInfo.bpaySalesAmount)}원</div>
              </li>
              <li className="small">
                <div>
                  <span>모바일 결제</span>
                  <span>
                    {getCommaNumber(
                      searchPeriodType === "daily"
                        ? totalInfo.searchBpayAppSalesAmount
                        : summaryInfo.bpayAppSalesAmount
                    )}
                    원
                  </span>
                </div>
                <div>
                  <span>키오스크 결제</span>
                  <span>
                    {getCommaNumber(
                      searchPeriodType === "daily"
                        ? totalInfo.searchBpayKioskSalesAmount
                        : summaryInfo.bpayKioskSalesAmount
                    )}
                    원
                  </span>
                </div>
              </li>
              <li>
                <div>현장판매</div>
                <div>{getCommaNumber(summaryInfo.crmSalesAmount)}원</div>
              </li>
              <li className="small">
                <div>
                  <span>수기 결제</span>
                  <span>
                    {getCommaNumber(
                      searchPeriodType === "daily"
                        ? totalInfo.searchCrmManualSalesAmount
                        : summaryInfo.crmManualSalesAmount
                    )}
                    원
                  </span>
                </div>
                <div>
                  <span>키오스크 결제</span>
                  <span>
                    {getCommaNumber(
                      searchPeriodType === "daily"
                        ? totalInfo.searchCrmKioskSalesAmount
                        : summaryInfo.crmKioskSalesAmount
                    )}
                    원
                  </span>
                </div>
              </li>
              <li>
                <div>환불</div>
                <div className="red">
                  {getCommaNumber(summaryInfo.refundAmount * -1)}원
                </div>
              </li>
            </ul>
            <PeriodList />
          </div>
        </Wrapper>
      </Section>
    );
  };
  return render();
};

const Wrapper = styled.div`
  .top {
    padding-bottom: 20px;
  }
  .top a {
    display: inline-flex;
    margin-top: 4px;
    padding-right: 1.25em;
    background: url(${iconChevron}) no-repeat center right / 12px;
  }
  .top h2 {
    font-size: 28px;
    font-size: 700;
  }
  .top .summaryInfo {
    text-align: center;
  }
  .top .summaryInfo h4 {
    font-size: 20px;
    font-weight: 400;
  }
  .top .chartInfo {
    min-height: 150px;
  }

  .middle > ul > li {
    display: flex;
    justify-content: space-between;
    padding: 16px 0;
    border-top: 1px solid var(--lightBorder);
  }
  .middle > ul > li div + div {
    font-weight: 600;
  }
  .middle > ul > li h4 {
    font-size: 1rem;
    font-weight: 500;
  }
  .middle > ul > li button {
    padding: 10px;
    width: 100%;
    background-color: white;
    border: none;
    outline: none;
    color: var(--darkGray);
  }

  .middle > ul > li.small {
    display: flex;
    flex-direction: column;
    width: 100%;
    border-top: none;
    padding: 0;
    padding-bottom: 16px;
    margin-top: -14px;
    padding-left: 6px;
    color: #7e868dff;
  }

  .middle > ul > li.small > div {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 500;
  }
`;

export default PeriodInfo;
